/* eslint-disable camelcase */
import React, {
  useEffect, useState,
} from 'react';
import { Box, styled, Typography } from '@mui/material';
import Modal from '../../components/modals/modal';
import { MapView } from './map-view';
import AutoCompleteSearchField from '../material_components/autocomplete-search-field';
import {
  currentMapZoom,
  getGoogleAutocompletePredictions, getGoogleMapPlace, GoogleMapPlaceResult,
} from './google-maps-helper';
import { CollapsableAlert } from '../material_components/collapsable-alert';
import { defaultExtents, Extents, LatLng } from '../../types/geo';

interface MapSearchSelection {
  description?: string;
  placeId?: string,
  secondaryText?: string,
}

const StyledBox = styled(Box)({
  boxSizing: 'border-box',
  height: '420px',
  border: '0.5px solid #C7C7CC',
  borderRadius: '8px',
  marginBottom: '1.375rem',
});

const InfoBox = styled(Box)({
  height: '80px',
  padding: '16px',
  borderRadius: '4px',
  marginBottom: '2',
  backgroundColor: '#F5F5F5',
});

const LocationTypography = styled(Typography)({
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  color: 'rgba(0, 0, 0, 0.6)',
  backgroundColor: '#F5F5F5',
  lineHeight: '24px',
});

interface Props {
  extents: Extents,
  title: string,
  open: boolean,
  label: string,
  onCloseClick: () => void,
  onNewLocationSet: (response: GoogleMapPlaceResult) => void,
  saveMapExtents: (extents: Extents) => void,
}

const SEARCH_FOCUS = true;

const MapViewDialog: React.FC<Props> = (
  {
    extents,
    title,
    open,
    label,
    onCloseClick,
    onNewLocationSet,
    saveMapExtents,
  },
) => {
  const [clickedPlace, setClickedPlace] = useState<LatLng>({ lat: 0, lng: 0 });
  const [openMapDialog, setOpenMapDialog] = useState<boolean>(open);
  const [addressSearchKey, setAddressSearchKey] = useState<string>('');
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<any[]>([]);
  const [addressSelection, setAddressSelection] = useState<MapSearchSelection | undefined>();
  const [currentExtents, setCurrentExtents] = useState<Extents>(extents || defaultExtents);
  const { latitude, longitude } = currentExtents;
  const [infoBoxCoordinates, setInfoBoxCoordinates] = useState<GoogleMapPlaceResult>(
    { coordinates: { lat: latitude, lng: longitude }, label: '', zoom: currentMapZoom(currentExtents) },
  );

  useEffect(() => {
    setOpenMapDialog(open);
  }, [open]);

  useEffect(() => {
    if (label) {
      setInfoBoxCoordinates({
        ...infoBoxCoordinates,
        label,
      });
    }
  }, [label]);

  useEffect(() => {
    // @ts-ignore
    const service = new google.maps.places.AutocompleteService();
    if (addressSearchKey.length) {
      service.getQueryPredictions({ input: addressSearchKey },
        (predictions, status) => {
          const results = getGoogleAutocompletePredictions(predictions, status);
          setAutoCompleteOptions(results);
        });
    } else {
      setAutoCompleteOptions([]);
    }
  }, [addressSearchKey]);

  useEffect(() => {
    // @ts-ignore
    const geocoder = new google.maps.Geocoder();
    // @ts-ignore
    const latLngGeo = new google.maps.LatLng(latitude, longitude);
    const requestObject = addressSelection
      ? { placeId: addressSelection.placeId }
      : { location: latLngGeo };

    if (addressSelection) {
      geocoder.geocode(
        requestObject,
        (result, status) => {
          const response = getGoogleMapPlace(result, status);
          setInfoBoxCoordinates(response);
          const newExtents = {
            ...currentExtents,
            latitude: response.coordinates.lat,
            longitude: response.coordinates.lng,
            zoom: currentMapZoom(currentExtents, SEARCH_FOCUS),
          };
          setCurrentExtents(newExtents);
          saveMapExtents(newExtents);
        },
      );
    }
  }, [addressSelection]);

  useEffect(() => {
    // @ts-ignore
    const geocoder = new google.maps.Geocoder();
    if (clickedPlace.lat !== 0 && clickedPlace.lng !== 0) {
      // @ts-ignore
      const clickedLatLng = new google.maps.LatLng(clickedPlace.lat, clickedPlace.lng);
      geocoder.geocode(
        { location: clickedLatLng },
        (result, status) => {
          const response = getGoogleMapPlace(result, status);
          setInfoBoxCoordinates({ ...response, coordinates: clickedPlace });
        },
      );
    }
  }, [clickedPlace.lat, clickedPlace.lng]);

  const handleClose = (): void => {
    onCloseClick();
    setInfoBoxCoordinates({
      ...infoBoxCoordinates,
      label,
    });
    setOpenMapDialog(false);
  };

  const handleSubmitSelection = () => {
    onNewLocationSet(infoBoxCoordinates);
    onCloseClick();
  };

  const handleAddressSelection = (value: any) => {
    const selection = value
      ? {
        description: value.description,
        placeId: value.place_id,
        secondaryText: value.structured_formatting.secondary_text,
      }
      : {};
    setAddressSelection(selection);
  };

  const handleMapClick = (latlng: LatLng) => {
    setClickedPlace(latlng);
  };

  return (
    <>
      <Modal
        title={title}
        onPositive={handleSubmitSelection}
        onNegative={handleClose}
        positive="Set location"
        negative="Cancel"
        maxWidth="md"
        serverError={false}
        open={openMapDialog}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
          <CollapsableAlert
            alertId="edit-map"
            alertText="Click on the map to set the issue location"
            actionIcon="InfoTwoToneIcon"
          />
          <Box mb={2}>
            <AutoCompleteSearchField
              label="Search address"
              getPredictions={(input = '') => setAddressSearchKey(input)}
              autoCompleteOptions={autoCompleteOptions}
              handleAddressSelection={handleAddressSelection}
            />
          </Box>
          <StyledBox mb={2}>
            <MapView
              mapExtents={currentExtents}
              saveMapExtents={saveMapExtents}
              allowAddingMarker
              onAddMarker={handleMapClick}
              marker={{ lat: currentExtents.latitude, lng: currentExtents.longitude }}
            />
          </StyledBox>
          <InfoBox>
            <Typography color="rgba(0, 0, 0, 0.87)">{`${infoBoxCoordinates.coordinates.lat}, ${infoBoxCoordinates.coordinates.lng}`}</Typography>
            <LocationTypography>{infoBoxCoordinates.label || "You haven't set a location"}</LocationTypography>
          </InfoBox>
        </Box>

      </Modal>
    </>
  );
};
export default MapViewDialog;
